/**
 * @license CKCode
 * Copyright 2023 CourseKata
 * SPDX-License-Identifier: GPL-3.0-or-later
 */

// import "@material/web/button/filled-button.js";
// import "@material/web/button/filled-tonal-button.js";
// import "@material/web/iconbutton/icon-button.js";

import { EditorView } from "@codemirror/view";
import { CKCodeCell } from "@coursekata/ckcode/lib/code-cell/code-cell.js";
import { IEditor, LitRenderable } from "@coursekata/ckcode/lib/code-cell/interfaces/index.js";
import { logos } from "@coursekata/ckcode/lib/code-cell/internal/logos.js";
import { parseKeyBindings } from "@coursekata/ckcode/lib/code-cell/key-binding.js";
import {
  IExerciseSpec,
  IPerishableItem,
  IRunResponse,
  IStorage,
  ISubmitResponse,
  KernelLanguage,
} from "@coursekata/ckcode/lib/core/interfaces/index.js";
import { CodeMirrorEditor } from "@coursekata/ckcode/lib/editor/internal/codemirror-editor.js";
import { PerishableBrowserStorage } from "@coursekata/ckcode/lib/storage/perishable-browser-storage.js";
import { renderMarkdown } from "@coursekata/ckcode/lib/utils.js";
import { JSONValue } from "@lumino/coreutils";
import { MdFilledTonalButton } from "@material/web/button/filled-tonal-button.js";
import { PropertyValueMap, html } from "lit";
import { customElement, state } from "lit/decorators.js";
import { classMap } from "lit/directives/class-map.js";
import { Ref, createRef, ref } from "lit/directives/ref.js";
import { styles } from "./cell-styles.css.js";

declare global {
  interface HTMLElementTagNameMap {
    "code-cell-previewer": CKCodeCellPreviewer;
  }
}

type PreviewerState = {
  language: KernelLanguage;
  spec: IExerciseSpec;
};

type EditorName = "setup" | "prompt" | "solution" | "test";

type AttributeMap = Record<"name" | "value", string>[];

const isString = (value: unknown): value is string =>
  typeof value === "string" || value instanceof String;

@customElement("code-cell-previewer")
export class CKCodeCellPreviewer extends CKCodeCell {
  // static properties required by Lit
  static override tag = "code-cell-previewer";
  static override styles = [styles, CKCodeCell.styles];

  // internal state
  @state() protected editing = true;

  // internal properties
  protected initialAttributes: AttributeMap;

  protected get storedLanguage(): KernelLanguage | null {
    return this.stateStorage.getItem("state")?.language;
  }

  protected get storedSpec(): IExerciseSpec | null {
    return this.stateStorage.getItem("state")?.spec;
  }

  get editors(): Record<EditorName, CodeMirrorEditor | undefined> {
    return {
      setup: this.setupEditorRef.value,
      prompt: this.promptEditorRef.value,
      solution: this.solutionEditorRef.value,
      test: this.testEditorRef.value,
    };
  }

  // storage
  stateStorage: IStorage<PreviewerState>;

  /** Setup the cell's storage. */
  protected override connectStorage(): void {
    // storage when in preview mode
    const prefixParts = ["ckcode-previewer", this.config.storagePrefix ?? "", this.id ?? ""];
    const prefix = `${prefixParts.join("--")}--`;
    this.storage = new PerishableBrowserStorage<
      (Omit<IRunResponse, "requester"> | Omit<ISubmitResponse, "requester">) & IPerishableItem
    >({ prefix }, this.log);

    // storage when in authoring mode
    this.stateStorage = new PerishableBrowserStorage<PreviewerState & IPerishableItem>(
      { prefix: "ckcode-previewer-", maxAge: 24 * 60 * 60 * 1000 },
      this.log
    );
  }

  // Actions
  /** Switch between editing and previewing modes. */
  toggleMode(): void {
    this.editing = !this.editing;
  }

  /** Copy the editors' text to a valid CodeCell markdown specification. */
  copy(): void {
    const markdown = this.exportToMarkdown();
    const errorMessage = "Could not copy markdown";

    const fallbackCopy = () => {
      const textarea = document.createElement("textarea");
      textarea.value = markdown;

      // Avoid scrolling to bottom
      textarea.style.top = "0";
      textarea.style.left = "0";
      textarea.style.position = "fixed";

      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();

      try {
        const copied = document.execCommand("copy");
        if (!copied) throw new Error("Error using document.execCommand to copy");
      } catch (error) {
        this.log.error(errorMessage, error);
        alert(errorMessage);
      }

      document.body.removeChild(textarea);
    };

    if (!navigator.clipboard) return fallbackCopy();
    navigator.clipboard.writeText(markdown).catch((error) => {
      this.log.error(errorMessage, error);
      alert(errorMessage);
    });
  }

  override async reset(): Promise<void> {
    super.reset();
    if (this.editing) {
      this.stateStorage.clear();
      this.setupEditorRef.value?.clear();
      this.promptEditorRef.value?.clear();
      this.solutionEditorRef.value?.clear();
      this.testEditorRef.value?.clear();
      this._applyAttributes(this.initialAttributes);
    }
  }

  /** Save the editors' state. */
  async save(): Promise<void> {
    this.stateStorage.setItem("state", { language: this.language, spec: this.spec });
  }

  // import / export helpers
  protected exportToMarkdown(): string {
    const code = [];
    const wrap = (name: string, content: string) => `%%% ${name}\n${content}`;
    const editors = this.editors;
    if (editors.setup?.text.trim()) code.push(wrap("setup", editors.setup?.text.trim()));
    if (editors.prompt?.text.trim()) code.push(wrap("prompt", editors.prompt?.text.trim()));
    if (editors.solution?.text.trim()) code.push(wrap("solution", editors.solution?.text.trim()));
    if (editors.test?.text.trim()) code.push(wrap("test", editors.test?.text.trim()));

    return `\`\`\`{ data-ckcode=true #${this.id} }\n` + code.join("\n\n") + "\n```";
  }

  protected handlePaste(lines: JSONValue, targetEditor: IEditor): void {
    if (!this.editors) return;

    let content: string;
    if (Array.isArray(lines) && lines.every((line) => isString(line))) {
      content = lines.join("\n");
    } else if (isString(lines)) {
      content = lines;
    } else {
      return;
    }

    if (!content.trim()) {
      return;
    }

    // extract the exercise spec from the (possibly rendered markdown) content
    const contentIsFenced = content.match(/\s*```.*?\n([\s\S]*)```\s*/);
    const renderedElement = contentIsFenced ? this._renderMarkdown(content) : null;
    content = renderedElement?.textContent ?? content;
    const spec = CKCodeCell.extractSpecFromMarkdown(content);

    // if we couldn't parse the markdown, just load the content into the target editor
    if (Object.keys(spec).length === 0) {
      targetEditor.text = content;
      return;
    }

    // otherwise, load the parsed spec into the editors
    this.reset();
    this._applyAttributes(renderedElement?.firstElementChild?.attributes);
    this._applyAttributes(renderedElement?.firstElementChild?.firstElementChild?.attributes);
    for (const [key, value] of Object.entries(spec)) {
      const name = key as EditorName;
      const editor = this.editors[name];
      editor.text = value;
    }
  }

  // helpers
  private _renderMarkdown(content: string): HTMLElement {
    const element = document.createElement("div");
    element.innerHTML = renderMarkdown(content).trim();
    return element;
  }

  private _applyAttributes(attributes?: NamedNodeMap | AttributeMap) {
    if (!attributes) return;
    for (const attribute of attributes) {
      if (attribute.name === "data-ckcode") continue;
      this.setAttribute(attribute.name, attribute.value);
    }
  }

  // initialization
  constructor() {
    super();
    this.initialAttributes = [];
    for (const attribute of this.attributes) {
      this.initialAttributes.push({ name: attribute.name, value: attribute.value });
    }

    const newKeybindings = [
      {
        key: "Ctrl-s",
        mac: "Meta-s",
        run: () => this.saveButtonRef.value?.click(),
        preventDefault: true,
      },
    ];

    this.keybindings.push(...newKeybindings);
    this.parsedKeyBindings.push(...parseKeyBindings(this.keybindings));
  }

  override connectedCallback(): void {
    super.connectedCallback();
    this.language = this.storedLanguage ?? this.config.session.language;
    this.spec = this.storedSpec ?? {};
  }

  protected override firstUpdated(
    _changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>
  ): void {
    super.firstUpdated(_changedProperties);
    Object.entries(this.editors).forEach(([name, editor]) => {
      editor.text = this.spec[name as EditorName] ?? "";
      editor.addExtension(
        EditorView.updateListener.of((update) => {
          update.transactions.forEach((transaction) => {
            if (transaction.docChanged) {
              this.updateCode();
              if (
                (this.isSubmittable() && !this.submitButtonRef.value) ||
                (!this.isSubmittable() && this.submitButtonRef.value)
              ) {
                this.requestUpdate();
              }
            }

            if (transaction.isUserEvent("input.paste")) {
              const textOnly: string[] = transaction.changes
                .toJSON()
                .flat()
                .filter((x: JSONValue) => isString(x));
              this.handlePaste(textOnly, editor);
            }
          });
        })
      );
    });
  }

  protected updateCode(): void {
    this.spec = {
      setup: this.setupEditorRef.value?.text,
      prompt: this.promptEditorRef.value?.text,
      solution: this.solutionEditorRef.value?.text,
      test: this.testEditorRef.value?.text,
    };

    this.editor.text = this.spec.prompt ?? "";
  }

  // rendering
  protected setupEditorRef: Ref<CodeMirrorEditor> = createRef();
  protected promptEditorRef: Ref<CodeMirrorEditor> = createRef();
  protected solutionEditorRef: Ref<CodeMirrorEditor> = createRef();
  protected testEditorRef: Ref<CodeMirrorEditor> = createRef();

  protected override renderEditor() {
    return html`
      <div class=${classMap({ hidden: this.editing })}>${super.renderEditor()}</div>
      <div class=${classMap({ hidden: !this.editing, "multi-editor": true })}>
        <label>
          <code>%%% setup</code>
          <ck-editor
            class="editor--setup"
            ${ref(this.setupEditorRef)}
            theme=${this.config.theme}
            language=${this.language}
          ></ck-editor>
        </label>

        <label>
          <code>%%% prompt</code>
          <ck-editor
            class="editor--prompt"
            ${ref(this.promptEditorRef)}
            theme=${this.config.theme}
            language=${this.language}
          ></ck-editor>
        </label>

        <label>
          <code>%%% solution</code>
          <ck-editor
            class="editor--solution"
            ${ref(this.solutionEditorRef)}
            theme=${this.config.theme}
            language=${this.language}
          ></ck-editor>
        </label>

        <label>
          <code>%%% test</code>
          <ck-editor
            class="editor--test"
            ${ref(this.testEditorRef)}
            theme=${this.config.theme}
            language=${this.language}
          ></ck-editor>
        </label>
      </div>
    `;
  }

  protected saveButtonRef: Ref<MdFilledTonalButton> = createRef();
  protected copyButtonRef: Ref<MdFilledTonalButton> = createRef();
  protected toggleButtonRef: Ref<MdFilledTonalButton> = createRef();
  protected override renderEditorControls(): LitRenderable {
    this.config.enableSolution = true;

    const output: LitRenderable[] = [];

    if (!this.editing) {
      output.push(super.renderEditorControls());
    } else {
      output.push(
        html`<md-filled-tonal-button ${ref(this.saveButtonRef)} @click=${this.save}
          >Save</md-filled-tonal-button
        >`
      );
      output.push(
        html`<md-filled-tonal-button ${ref(this.copyButtonRef)} @click=${this.copy}
          >Copy</md-filled-tonal-button
        >`
      );
      output.push(
        html`<md-filled-tonal-button ${ref(this.resetButtonRef)} @click=${this.reset}
          >Reset</md-filled-tonal-button
        >`
      );
    }

    const toggleName = this.editing ? "Preview" : "Author";
    output.push(
      html`<md-filled-button ${ref(this.toggleButtonRef)} @click=${this.toggleMode}
        >${toggleName}</md-filled-button
      >`
    );
    return output;
  }

  protected override renderExerciseId(): LitRenderable {
    if (!this.editing) return super.renderExerciseId();
    return html` <label>
      Exercise ID:
      <input type="text" value=${this.id} @change=${this.handleExerciseIdChange} />
    </label>`;
  }

  protected handleExerciseIdChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.id = target.value;
  }

  protected override renderKernelLogo(): LitRenderable {
    const activeLogoTemplate = super.renderKernelLogo();
    if (!this.editing) return activeLogoTemplate;

    const r = logos[KernelLanguage.r];
    const python = logos[KernelLanguage.python];
    const pythonClasses = { selected: this.language === KernelLanguage.python };
    const rClasses = { selected: this.language === KernelLanguage.r };

    return html`
      <md-standard-icon-button
        title="Use Python"
        class=${classMap(pythonClasses)}
        @click=${() => (this.language = KernelLanguage.python)}
      >
        ${python}
      </md-standard-icon-button>
      <md-standard-icon-button
        title="Use R"
        class=${classMap(rClasses)}
        @click=${() => (this.language = KernelLanguage.r)}
      >
        ${r}
      </md-standard-icon-button>
    `;
  }
}
